import { useNavigate } from 'react-router-dom';
import Pagination from '../Common/Pagination';
import React, { useState, useMemo } from 'react';
import { ArrowLeft } from 'lucide-react';

interface ViewForWarehousesProps {
  product?: any;
  onBack: () => void;
}

interface WarehouseAssignment {
  userId: number;
  name: string;
  mrp: string;
  price: string;
  forceSell: boolean;
}

const mockAssignments: WarehouseAssignment[] = [
  {
    userId: 1,
    name: 'Vashi Depo - 557',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 2,
    name: 'Kharia Distributors - 8000222',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 3,
    name: 'Dhule Dairy - 8000236',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 4,
    name: 'Harshal Patil-Kandivali - 8000848',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 5,
    name: 'Kanchan Dairy Farm-Malad - 8000838',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 6,
    name: 'Lata Enterprises - 6000709',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 7,
    name: 'MAURYA DAIRY - 8000589',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 8,
    name: 'Maurya Enterprises-Malad - 8000886',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 9,
    name: 'Samwit Milk Supplier - 6000934',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  },
  {
    userId: 10,
    name: 'Shiva Milk Agency-Goregaon - 8000880',
    mrp: '360.000',
    price: '343.200',
    forceSell: false,
  }
];

const ViewForWarehouses: React.FC<ViewForWarehousesProps> = ({ product, onBack }) => {
  const [searchQuery, setSearchQuery] = useState('');
  const [entriesLimit, setEntriesLimit] = useState(25);
  const [currentPage, setCurrentPage] = useState(1);
  const navigate = useNavigate();

  // Edit State
  const [editingRowId, setEditingRowId] = useState<number | null>(null);
  const [editFormData, setEditFormData] = useState({ mrp: '', price: '', forceSell: false });

  const productCode = product?.skuCode || '85000000';
  const productName = product?.name || 'GOW. GOLD COW MILK 6000 ML';

  // Filtered items
  const filteredAssignments = useMemo(() => {
    return mockAssignments.filter((item) => {
      if (searchQuery.trim() !== '') {
        const query = searchQuery.toLowerCase();
        return (
          item.name.toLowerCase().includes(query) ||
          item.mrp.toLowerCase().includes(query) ||
          item.price.includes(query)
        );
      }
      return true;
    });
  }, [searchQuery]);

  // Paginated items
  const paginatedAssignments = useMemo(() => {
    const startIndex = (currentPage - 1) * entriesLimit;
    return filteredAssignments.slice(startIndex, startIndex + entriesLimit);
  }, [filteredAssignments, currentPage, entriesLimit]);

  const handleEditClick = (wh: WarehouseAssignment) => {
    setEditingRowId(wh.userId);
    setEditFormData({ mrp: wh.mrp, price: wh.price, forceSell: wh.forceSell });
  };

  const handleSave = () => {
    // Here you would normally update the actual data array or make an API call
    setEditingRowId(null);
  };

  const handleCancel = () => {
    setEditingRowId(null);
  };

  const handleBack = () => {
    if (onBack) { onBack(); } else { navigate(-1); }
  };

  return (
    <div className="space-y-6">

      {/* Sub-Header Row */}
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">

        {/* Left Back and Title */}
        <div className="flex items-center gap-3">
          <button onClick={handleBack}
            className="w-8 h-8 rounded-full bg-[#C1272D] hover:bg-[#A81E23] transition-all flex items-center justify-center text-white cursor-pointer shadow-sm"
            title="Go Back">
            <ArrowLeft className="w-4 h-4" />
          </button>
          <h1 className="text-2xl font-extrabold text-[#C1272D] tracking-tight m-0">
            View for warehouse
          </h1>
        </div>

        {/* Right side Product Label */}
        <div className="text-base font-semibold text-[#333]">
          Product: {productCode} - {productName}
        </div>

      </div>

      {/* Main Table Card wrapper */}
      <div className="bg-white rounded-2xl shadow-sm border border-[#ECECF2] overflow-hidden">

        {/* Toolbar */}
        <div className="px-4 py-3 border-b border-[#ECECF2] flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 bg-white">

          <div className="flex items-center gap-2">
            <span className="text-sm font-semibold text-black">Show</span>
            <select
              value={entriesLimit}
              onChange={(e) => setEntriesLimit(Number(e.target.value))}
              className="bg-white border border-gray-300 text-xs font-semibold text-black rounded-xl px-2 py-1 outline-none cursor-pointer transition-colors"
            >
              <option value={5}>5</option>
              <option value={10}>10</option>
              <option value={25}>25</option>
              <option value={50}>50</option>
            </select>
            <span className="text-sm font-semibold text-black">entries</span>
          </div>

          <div className="flex flex-wrap items-center gap-4">
            {/* Search */}
            <div className="relative">
              <input
                type="text"
                placeholder="Search"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="bg-white border border-gray-300 text-sm rounded-full py-1 px-4 outline-none transition-all"
              />
            </div>
          </div>

        </div>

        {/* CSS GRID SCROLLABLE TABLE */}
        <div className="overflow-x-auto">

          {/* Grid Headers with yellow background */}
          <div className="grid grid-cols-[80px_1fr_120px_120px_150px_180px] items-center px-6 py-4 bg-[#FFF8E1] border-b border-[#ECECF2] min-w-[900px]">
            <div className="text-xs font-semibold text-[#1F1F1F]">User ID</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Name</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">MRP</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Price</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Force Sell</div>
            <div className="text-xs font-semibold text-[#1F1F1F]">Action</div>
          </div>

          {/* Grid Body Rows */}
          <div className="divide-y divide-[#ECECF2] min-w-[900px]">
            {paginatedAssignments.length > 0 ? (
              paginatedAssignments.map((wh) => (
                <div
                  key={wh.userId}
                  className="grid grid-cols-[80px_1fr_120px_120px_150px_180px] items-center px-6 py-4 transition-colors"
                >
                  {/* Line # */}
                  <div className="text-xs text-[#828282]">
                    {wh.userId}
                  </div>

                  {/* Name */}
                  <div className="text-xs text-[#828282]">
                    {wh.name}
                  </div>

                  {/* MRP */}
                  <div>
                    {editingRowId === wh.userId ? (
                      <input
                        type="text"
                        value={editFormData.mrp}
                        onChange={(e) => setEditFormData({ ...editFormData, mrp: e.target.value })}
                        className="bg-[#E2E2E2] text-xs text-[#828282] px-4 py-1.5 rounded-full w-24 outline-none"
                      />
                    ) : (
                      <span className="text-xs text-[#828282]">{wh.mrp}</span>
                    )}
                  </div>

                  {/* Price */}
                  <div>
                    {editingRowId === wh.userId ? (
                      <input
                        type="text"
                        value={editFormData.price}
                        onChange={(e) => setEditFormData({ ...editFormData, price: e.target.value })}
                        className="bg-[#E2E2E2] text-xs text-[#828282] px-4 py-1.5 rounded-full w-24 outline-none border border-transparent focus:border-slate-400"
                      />
                    ) : (
                      <span className="text-xs text-[#828282]">{wh.price}</span>
                    )}
                  </div>

                  {/* Force Sell */}
                  <div>
                    {editingRowId === wh.userId ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={() => setEditFormData({ ...editFormData, forceSell: true })}
                          className={`px-4 py-2 rounded-xl text-xs text-[#828282] transition-colors cursor-pointer ${editFormData.forceSell ? 'go_erp-bg text-white' : 'bg-[#FFF8E1] text-[#7A7A7A]'}`}
                        >
                          Yes
                        </button>
                        <button
                          onClick={() => setEditFormData({ ...editFormData, forceSell: false })}
                          className={`px-4 py-2 rounded-xl text-xs text-[#828282] transition-colors cursor-pointer ${!editFormData.forceSell ? 'go_erp-bg text-white' : 'bg-[#FFF8E1] text-[#7A7A7A]'}`}
                        >
                          No
                        </button>
                      </div>
                    ) : (
                      <span className="text-xs text-[#828282]">{wh.forceSell ? 'Yes' : 'No'}</span>
                    )}
                  </div>

                  {/* Action */}
                  <div>
                    {editingRowId === wh.userId ? (
                      <div className="flex items-center gap-2">
                        <button
                          onClick={handleSave}
                          className="bg-[#FBC02D] text-white text-xs px-4 py-2 rounded-xl cursor-pointer transition-colors"
                        >
                          Save
                        </button>
                        <button
                          onClick={handleCancel}
                          className="bg-[#6D4C41] text-white text-xs px-4 py-2 rounded-xl cursor-pointer transition-colors"
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <button
                        onClick={() => handleEditClick(wh)}
                        className="go_erp-bg text-white text-xs px-4 py-2 rounded-xl cursor-pointer transition-colors"
                      >
                        Edit
                      </button>
                    )}
                  </div>
                </div>
              ))
            ) : (
              <div className="px-6 py-10 text-center text-xs font-semibold text-slate-400">
                No warehouse assignments found matching search query.
              </div>
            )}
          </div>

        </div>

        {/* Pagination Footer */}
        <Pagination
          currentPage={currentPage}
          totalEntries={filteredAssignments.length}
          entriesLimit={entriesLimit}
          onPageChange={setCurrentPage}
        />

      </div>

    </div>
  );
};

export default ViewForWarehouses;